import React, {
    FocusEventHandler,
    KeyboardEvent,
    useCallback,
    useEffect,
} from 'react';

import './SymbolInput.scss';
import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';
import { useSymbolValidation } from 'symbols/hooks/useSymbolValidation';
import { MessageIcon } from '@instinet-analytics/semantic-ui-components';
import { AutoSuggest } from '@instinet-analytics/core-lib';
import classNames from 'classnames';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';

export interface SymbolInputProps {
    className?: string;
    autofocus?: boolean;
    value: string | undefined;
    suppressInvalidMessages?: boolean;
    placeholder?: string;
    symbology?: SymbologyValues;
    onChange: (symbol: string) => void;
    onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
    onBlur?: FocusEventHandler<HTMLElement>;
    onValidationStatusChanged?: (
        value: string | undefined,
        isValid: boolean
    ) => void;
}

export default function SymbolInput(props: SymbolInputProps) {
    const {
        autofocus,
        className,
        value,
        onChange: onChangeProps,
        suppressInvalidMessages,
        symbology: symbologyProps,
        placeholder: placeholderProps,
        onKeyDown,
        onBlur,
        onValidationStatusChanged,
    } = props;
    const { symbology: symbologyUser } = useSelectAppSettings();

    const symbology = symbologyProps ?? symbologyUser;

    const placeHolder = placeholderProps
        ? placeholderProps
        : `Enter a${
              symbology === SymbologyValues.Instinet ? 'n' : ''
          } ${symbology} symbol`;

    const { symbolInvalid, result: symbolValidationResult } =
        useSymbolValidation(value, symbology);

    useEffect(() => {
        if (onValidationStatusChanged) {
            onValidationStatusChanged(value, !symbolInvalid);
        }
    }, [onValidationStatusChanged, value, symbolInvalid]);

    const onChanged = (enteredText: string) => {
        const nextSymbol = enteredText.toUpperCase();
        onChangeProps(nextSymbol);
    };

    const cssClasses = classNames(
        'SymbolInput',
        !suppressInvalidMessages && symbolInvalid && 'data-invalid',
        className
    );

    const onGetSuggestion = useCallback((c: string) => c, []);
    const onGetSuggestionValue = useCallback((c: string) => c, []);

    return (
        <div className={cssClasses}>
            <AutoSuggest
                autoFocus={autofocus === undefined ? true : autofocus}
                className="AutoSuggestSymbol"
                candidates={symbolValidationResult.matches}
                error={!suppressInvalidMessages && symbolInvalid}
                value={value ?? ''}
                getComparisonString={onGetSuggestion}
                getCandidateValue={onGetSuggestionValue}
                onChange={onChanged}
                placeholder={placeHolder}
                onKeyDown={onKeyDown}
                onBlur={onBlur}
            />

            {!suppressInvalidMessages && symbolInvalid && (
                <MessageIcon
                    title={`${symbolValidationResult.symbol} is not a valid ${symbolValidationResult.symbology} symbol`}
                />
            )}
        </div>
    );
}
